import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getIndustryPage } from '../lib/industryPages';
import { productPages } from '../lib/productPages';
import { useDialog } from '../contexts/DialogContext';

export const IndustryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { openDialog } = useDialog();
  const page = slug ? getIndustryPage(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (page) {
      document.title = page.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', page.metaDescription);
    }
  }, [slug, page]);

  if (!page) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-32 text-center">
        <h1 className="text-4xl font-bold" style={{ color: '#241E61' }}>Page not found</h1>
        <p className="mt-4 text-gray-600">The industry page you're looking for doesn't exist.</p>
        <Link to="/" className="mt-8 inline-block rounded-md px-6 py-3 text-sm font-semibold text-white" style={{ backgroundColor: '#241E61' }}>
          Back to home
        </Link>
      </div>
    );
  }

  const relatedProducts = page.relatedProductSlugs
    .map(s => productPages.find(p => p.slug === s))
    .filter(Boolean);

  return (
    <div className="pt-24">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-4">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
          <span>/</span>
          <span>Industries</span>
          <span>/</span>
          <span style={{ color: '#241E61' }} className="font-medium">{page.industry}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase" style={{ backgroundColor: '#f0fdf4', color: '#166534' }}>
            BrandedMark for {page.industry}
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl" style={{ color: '#241E61' }}>
            {page.headline}
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-8 max-w-2xl mx-auto">
            {page.subheadline}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => openDialog('waitlist')}
              className="rounded-md px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: '#241E61' }}
            >
              Join the Waitlist
            </button>
            <Link
              to="/pricing"
              className="rounded-md px-6 py-3.5 text-sm font-semibold transition-colors ring-1 ring-gray-300 hover:bg-gray-50"
              style={{ color: '#241E61' }}
            >
              See Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: '#241E61' }}>
              {page.problemHeadline}
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-8">
              {page.problemDescription}
            </p>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {page.problemStats.map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 text-center shadow-sm ring-1 ring-gray-100">
                <div className="text-4xl font-bold" style={{ color: '#241E61' }}>{stat.value}</div>
                <p className="mt-3 text-sm text-gray-600 leading-6">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities (alternating sections) */}
      {page.capabilities.map((cap, i) => (
        <section key={i} className={`py-16 lg:py-24 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                <h3 className="text-2xl font-bold tracking-tight sm:text-3xl" style={{ color: '#241E61' }}>
                  {cap.title}
                </h3>
                <p className="mt-4 text-gray-600 leading-7">
                  {cap.description}
                </p>
                <ul className="mt-8 space-y-3">
                  {cap.details.map((detail, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <svg className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="#00D084">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-600 text-sm leading-6">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-center px-8">
                    <div className="w-16 h-16 rounded-xl mx-auto flex items-center justify-center" style={{ backgroundColor: '#241E61' }}>
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                      </svg>
                    </div>
                    <p className="mt-4 text-sm font-medium text-gray-400">{cap.title}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* How it works — Product Example */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: '#241E61' }}>
              How it works for a {page.productExample.productType}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {page.productExample.steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-100 relative">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mb-4" style={{ backgroundColor: '#241E61' }}>
                  {i + 1}
                </div>
                <h3 className="font-semibold" style={{ color: '#241E61' }}>{step.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-6">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: '#241E61' }}>
            {page.ctaHeadline}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            {page.ctaDescription}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => openDialog('waitlist')}
              className="rounded-md px-8 py-4 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: '#241E61' }}
            >
              Join the Waitlist
            </button>
            <a
              href="mailto:hello@brandedmark.com"
              className="rounded-md px-8 py-4 text-sm font-semibold transition-colors ring-1 ring-gray-300 hover:bg-gray-50"
              style={{ color: '#241E61' }}
            >
              Talk to Us
            </a>
          </div>
        </div>
      </section>

      {/* Related Features */}
      {relatedProducts.length > 0 && (
        <section className="bg-gray-50 py-16 lg:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="text-2xl font-bold tracking-tight text-center" style={{ color: '#241E61' }}>
              Related capabilities
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((related) => related && (
                <Link
                  key={related.slug}
                  to={`/product/${related.slug}`}
                  className="group bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-100 hover:ring-gray-300 transition-all"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#00D084' }}>
                    {related.tagline}
                  </span>
                  <h3 className="mt-2 font-semibold group-hover:opacity-80 transition-opacity" style={{ color: '#241E61' }}>
                    {related.name}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{related.subheadline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
